import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ChannelService } from 'src/app/services/channel.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'tenant-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzTabsModule,
    NzSpinModule,
    NzIconModule,
    NzDividerModule,
    NzTagModule,
    NzDescriptionsModule,
    NzGridModule,
    NzToolTipModule,
    NzModalModule,
    NzSwitchModule,
    NzUploadModule,
    NzAvatarModule,
    NzEmptyModule,
    NzDatePickerModule,
    NzInputNumberModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-config.component.html',
  styleUrls: ['./tenant-config.component.less']
})
export class TenantConfigComponent implements OnInit, OnChanges {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly channelService = inject(ChannelService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  @Input() tenantId: string = '';
  @Input() tenantType: string = '';
  @Input() standalonePage = false;

  get isPersonalTenant(): boolean {
    const type = this.tenantType || this.tenantModel?.type;
    return type === 'Person';
  }

  get shouldShowInactivityConfig(): boolean {
    return !this.isPersonalTenant;
  }

  tenantConfig: any;
  configForm: FormGroup = this.fb.group({});
  lineForm: FormGroup = this.fb.group({
    channelId: [null, [Validators.required]],
    channelSecret: [null, [Validators.required]],
    providerId: [null, [Validators.required]]
  });
  configSaving = false;
  configLoading = false;
  bindingLoading = false;
  unbindLoading = false;
  lineModalVisible = false;
  officialServiceNumberId = '';
  lineLoginInfo: any = null;
  tenantModel: any = null;
  groupedConfigParams: Array<{ groupName: string; params: any[] }> = [];
  dictionaryOptionsCache: { [key: string]: any[] } = {};

  inactivityForm: FormGroup = this.fb.group({
    enabled: [false, [Validators.required]],
    inactiveDays: [30, [Validators.required, Validators.min(7), Validators.max(365)]]
  });
  inactivityLoading = false;
  inactivitySaving = false;
  inactivityInitialValue = { enabled: false, inactiveDays: 30 };

  constructor() {}

  ngOnInit(): void {
    this.loadTenantConfig();
    this.loadLineLoginInfo();
    if (this.shouldShowInactivityConfig) {
      this.loadInactivityConfig();
    }
  }

  refreshTenantConfig(): void {
    this.loadTenantConfig();
    this.loadLineLoginInfo();
    if (this.shouldShowInactivityConfig) {
      this.loadInactivityConfig();
    }
  }

  get pageSpinning(): boolean {
    return this.configLoading || this.bindingLoading || this.inactivityLoading;
  }

  get isTenantAudited(): boolean {
    const certStatus = this.tenantModel?.certificateStatus ?? this.tenantModel?.certificate_status;
    return certStatus === 'Audited';
  }

  get inactivityEnabled(): boolean {
    return Boolean(this.inactivityForm.get('enabled')?.value);
  }

  get inactivitySavedEnabled(): boolean {
    return Boolean(this.inactivityInitialValue.enabled);
  }

  get inactivityStatusText(): string {
    return this.inactivitySavedEnabled
      ? this.i18n.fanyi('tenant.detail.inactivity-status-enabled')
      : this.i18n.fanyi('tenant.detail.inactivity-status-disabled');
  }

  get inactivityStatusClass(): string {
    return this.inactivitySavedEnabled ? 'is-bound' : '';
  }

  get isInactivityFormChanged(): boolean {
    const val = this.inactivityForm.getRawValue();
    return (
      Boolean(val.enabled) !== this.inactivityInitialValue.enabled || Number(val.inactiveDays) !== this.inactivityInitialValue.inactiveDays
    );
  }

  get visibleConfigGroups(): Array<{ groupName: string; params: any[] }> {
    return this.groupedConfigParams.filter(group => group.params.some(param => param.visible !== false));
  }

  get isLineLoginBound(): boolean {
    return this.hasLineLoginBindingData;
  }

  get hasLineLoginBindingData(): boolean {
    return !!(this.lineLoginInfo?.channelId || this.lineLoginInfo?.providerId || this.lineLoginInfo?.serviceNumberId);
  }

  get isLineLoginAvailable(): boolean {
    const status = this.lineLoginInfo?.status;
    if (!status) {
      return this.hasLineLoginBindingData;
    }
    return status === 'Available';
  }

  get lineSectionTitle(): string {
    return this.i18n.fanyi('tenant.detail.line-login-config-title');
  }

  get lineSectionDescription(): string {
    return this.i18n.fanyi('tenant.detail.line-login-config-description');
  }

  get lineCardDescription(): string {
    return this.hasLineLoginBindingData
      ? this.i18n.fanyi('tenant.detail.line-login-card-description-bound')
      : this.i18n.fanyi('tenant.detail.line-login-card-description-unbound');
  }

  get lineStatusText(): string {
    if (!this.hasLineLoginBindingData) {
      return this.i18n.fanyi('tenant.detail.line-login-status-unbound');
    }
    const status = this.lineLoginInfo?.status;
    if (status === 'Failed') {
      return this.i18n.fanyi('tenant.detail.line-login-status-failed');
    }
    if (status === 'Binding') {
      return this.i18n.fanyi('tenant.detail.line-login-status-binding');
    }
    if (status === 'Initializing') {
      return this.i18n.fanyi('tenant.detail.line-login-status-initializing');
    }
    return this.i18n.fanyi('tenant.detail.line-login-status-enabled');
  }

  get lineStatusClass(): string {
    const status = this.lineLoginInfo?.status;
    if (status === 'Failed') {
      return 'is-danger';
    }
    if (this.isLineLoginAvailable) {
      return 'is-bound';
    }
    return '';
  }

  get lineLoginFailureReason(): string {
    if (this.lineLoginInfo?.status !== 'Failed') {
      return '';
    }
    const rebindKey = 'tenant.detail.line-login-rebind-hint';
    const translatedRebindHint = this.i18n.fanyi(rebindKey);
    const rebindHint = translatedRebindHint === rebindKey ? '请重新绑定。' : translatedRebindHint;
    const reason = this.lineLoginInfo?.bindErrorMessage;
    if (!reason) {
      return rebindHint;
    }
    return /[。.!?！？]$/.test(reason) ? `${reason}${rebindHint}` : `${reason}。${rebindHint}`;
  }

  get lineEmptyText(): string {
    return this.officialServiceNumberId
      ? this.i18n.fanyi('tenant.detail.line-login-empty-unbound')
      : this.i18n.fanyi('tenant.detail.line-login-empty-no-official-service');
  }

  loadTenantConfig(): void {
    this.configLoading = true;
    this.http
      .post(`${ConstantUtil.TENANT_CONFIG_V1_TEANAT_FORM}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.configLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.tenantConfig = res.data;
          this.initConfigForm();
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('tenant.config.load-failed'));
        }
        this.cdr.detectChanges();
      });
  }

  loadLineLoginInfo(): void {
    this.bindingLoading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_TENANT_ITEM}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.bindingLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.officialServiceNumberId = res.data?.officialServiceNumberId || '';
          this.lineLoginInfo = res.data?.lineLoginModel || null;
          this.tenantModel = res.data?.tenantModel || res.data || null;
          this.updateInactivityFormState();
        } else {
          this.message.error(this.i18n.fanyi('tenant.detail.load-tenant-info-failed'));
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantId'] && !changes['tenantId'].firstChange) {
      this.loadTenantConfig();
      this.loadLineLoginInfo();
      if (this.shouldShowInactivityConfig) {
        this.loadInactivityConfig();
      }
    }
    if (changes['tenantType'] && !changes['tenantType'].firstChange) {
      if (this.shouldShowInactivityConfig) {
        this.loadInactivityConfig();
      }
    }
    if (changes['tenantConfig'] && !changes['tenantConfig'].firstChange) {
      this.initConfigForm();
    }
  }

  initConfigForm() {
    // 创建配置表单
    this.configForm = this.fb.group({});

    // 如果有配置参数
    if (this.tenantConfig && this.tenantConfig.params) {
      // 按组分类参数
      const groupMap = new Map<string, any[]>();

      this.tenantConfig.params.forEach(
        (param: {
          group?: string;
          code: string;
          type: string;
          value: any;
          defaultValue: any;
          disable: boolean;
          require: boolean;
          index?: number;
        }) => {
          const group = param.group;
          if (group && !groupMap.has(group)) {
            groupMap.set(group, []);
          }
          if (group) {
            const groupParams = groupMap.get(group);
            if (groupParams) {
              groupParams.push(param);
            }
          }

          // 为每个参数创建表单控件
          const validators = [];
          if (param.require) {
            validators.push(Validators.required);
          }

          // 根据类型设置默认值
          let defaultValue = param.value !== null ? param.value : param.defaultValue;
          if (param.type === 'Switch') {
            defaultValue = defaultValue === true || defaultValue === 'true';
          } else if (param.type === 'InputBox-Integer') {
            defaultValue = defaultValue !== null ? Number(defaultValue) : null;
          }

          this.configForm.addControl(
            param.code,
            this.fb.control(
              {
                value: defaultValue,
                disabled: param.disable
              },
              validators
            )
          );
        }
      );

      // 转换为数组格式，并按组名排序
      this.groupedConfigParams = Array.from(groupMap.entries())
        .map(([groupName, params]) => ({
          groupName,
          params: params.sort((a, b) => (a.index || 0) - (b.index || 0))
        }))
        .sort((a, b) => a.groupName.localeCompare(b.groupName));
    }
  }

  // 获取字典选项
  getDictionaryOptions(dictionaryId: string): any[] {
    if (!dictionaryId) {
      return [];
    }

    // 如果已缓存，直接返回
    if (this.dictionaryOptionsCache[dictionaryId]) {
      return this.dictionaryOptionsCache[dictionaryId];
    }

    // 这里应该调用API获取字典选项
    // 为简化示例，返回空数组
    this.dictionaryOptionsCache[dictionaryId] = [];
    return this.dictionaryOptionsCache[dictionaryId];
  }

  // 保存配置
  saveConfig() {
    if (this.configForm.invalid) {
      // 标记所有字段为已触摸，显示验证错误
      Object.values(this.configForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.message.warning(this.i18n.fanyi('tenant.config.complete_required_fields'));
      return;
    }

    this.configSaving = true;

    // 准备提交的数据
    const formValues = this.configForm.getRawValue();
    const keys: string[] = [];
    const values: any[] = [];

    this.tenantConfig.params.forEach((param: { code: string; [key: string]: any }) => {
      if (!param['disable']) {
        keys.push(param.code);
        values.push(formValues[param.code]);
      }
    });

    const requestData = {
      bundleId: this.tenantConfig.id,
      tenantId: this.tenantId,
      keys: keys,
      values: values
    };

    this.http
      .post(ConstantUtil.TENANT_CONFIG_V1_TEANAT_SAVE, requestData)
      .pipe(
        finalize(() => {
          this.configSaving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.config.save_success'));
          // 更新本地配置
          this.loadTenantConfig();
        } else {
          this.message.error(this.i18n.fanyi('tenant.config.save_failed'));
        }
      });
  }

  // 重置配置
  resetConfig() {
    // 重置为初始值
    this.tenantConfig.params.forEach((param: { code: string; value: any; defaultValue: any }) => {
      const control = this.configForm.get(param.code);
      if (control) {
        control.setValue(param.value !== null ? param.value : param.defaultValue);
        control.markAsPristine();
      }
    });
    this.message.info(this.i18n.fanyi('tenant.config.reset_to_current_value'));
  }

  showLineBindModal(): void {
    this.lineForm.reset({
      channelId: this.lineLoginInfo?.channelId ?? null,
      channelSecret: this.lineLoginInfo?.channelSecret ?? null,
      providerId: this.lineLoginInfo?.providerId ?? null
    });
    this.lineModalVisible = true;
  }

  handleLineCancel(): void {
    this.lineModalVisible = false;
  }

  bindLineLogin(): void {
    if (!this.officialServiceNumberId) {
      this.message.warning(this.i18n.fanyi('tenant.detail.line-login-not-bound'));
      return;
    }

    if (this.lineForm.invalid) {
      Object.values(this.lineForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      });
      return;
    }

    const formValue = this.lineForm.value;

    this.configSaving = true;
    this.channelService
      .bindLineLogin(this.officialServiceNumberId, formValue.channelId, formValue.channelSecret, formValue.providerId)
      .pipe(
        finalize(() => {
          this.configSaving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.detail.line-bind-success'));
          this.lineModalVisible = false;
          this.loadLineLoginInfo();
        } else {
          this.message.error(this.i18n.fanyi('tenant.detail.line-bind-failed'));
        }
      });
  }

  unbindLineLogin(): void {
    if (!this.officialServiceNumberId) {
      return;
    }

    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.detail.confirm-unbind'),
      nzContent: this.i18n.fanyi('tenant.detail.confirm-unbind-content'),
      nzOkText: this.i18n.fanyi('tenant.detail.confirm'),
      nzCancelText: this.i18n.fanyi('tenant.detail.cancel'),
      nzOnOk: () => this.executeLineUnbind()
    });
  }

  executeLineUnbind(): void {
    this.unbindLoading = true;
    this.channelService
      .unbindLineLogin(this.officialServiceNumberId)
      .pipe(
        finalize(() => {
          this.unbindLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.detail.line-unbind-success'));
          this.lineLoginInfo = null;
        } else {
          this.message.error(this.i18n.fanyi('tenant.detail.line-unbind-failed'));
        }
      });
  }

  copyValue(value: string | null | undefined): void {
    if (!value) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => this.message.success(this.i18n.fanyi('common.copy-success')))
        .catch(() => this.message.error(this.i18n.fanyi('common.copy-failed')));
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = value;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.message.success(this.i18n.fanyi('common.copy-success'));
  }

  updateInactivityFormState(): void {
    if (!this.isTenantAudited) {
      this.inactivityForm.disable({ emitEvent: false });
    } else {
      this.inactivityForm.enable({ emitEvent: false });
    }
    this.cdr.detectChanges();
  }

  loadInactivityConfig(): void {
    if (!this.shouldShowInactivityConfig) {
      return;
    }
    this.inactivityLoading = true;
    const body = this.tenantId ? { tenantId: this.tenantId } : {};
    this.http
      .post(ConstantUtil.TENANT_INACTIVITY_CONFIG_ITEM, body)
      .pipe(
        finalize(() => {
          this.inactivityLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            const data = res.data;
            const enabled = Boolean(data?.enabled);
            const inactiveDays = data?.inactiveDays ?? 30;
            this.inactivityInitialValue = { enabled, inactiveDays };
            this.inactivityForm.patchValue({ enabled, inactiveDays });
            this.inactivityForm.markAsPristine();
          } else {
            this.message.error(this.i18n.fanyi('tenant.detail.inactivity-load-failed'));
          }
          this.updateInactivityFormState();
        },
        error: () => {
          this.message.error(this.i18n.fanyi('tenant.detail.inactivity-load-failed'));
          this.updateInactivityFormState();
        }
      });
  }

  saveInactivityConfig(): void {
    if (!this.isTenantAudited) {
      this.message.warning(this.i18n.fanyi('tenant.detail.inactivity-uncertified-warning'));
      return;
    }
    if (this.inactivityForm.invalid) {
      Object.values(this.inactivityForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      });
      return;
    }

    const formValue = this.inactivityForm.getRawValue();
    const enabled = Boolean(formValue.enabled);
    const inactiveDays = Number(formValue.inactiveDays);

    this.inactivitySaving = true;
    const requestPayload: any = {
      enabled,
      inactiveDays
    };
    if (this.tenantId) {
      requestPayload.tenantId = this.tenantId;
    }
    this.http
      .post(ConstantUtil.TENANT_INACTIVITY_CONFIG_SAVE, requestPayload)
      .pipe(
        finalize(() => {
          this.inactivitySaving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.message.success(this.i18n.fanyi('tenant.detail.inactivity-save-success'));
            this.inactivityInitialValue = { enabled, inactiveDays };
            this.inactivityForm.markAsPristine();
          } else {
            this.message.error(res?.message || this.i18n.fanyi('tenant.detail.inactivity-save-failed'));
          }
        },
        error: (err: any) => {
          this.message.error(err?.message || this.i18n.fanyi('tenant.detail.inactivity-save-failed'));
        }
      });
  }
}
